/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useEffect, useRef, useState } from "react";
import { PropTypes } from "prop-types";
import styled, { css } from "styled-components";
import { FiraSansBold, FontWeight, Text } from '../Text';
import Icon from "../Icon";
import Colors from "constants/Colors";
import Label from "./Label";
import ValidationError from "./ValidationError";
import Spinner, { SpinnerSize } from "../Spinner";
import Button from "../Button";
import _ from "lodash";
import { Breakpoints } from "../../constants/Breakpoints";
import { mediaHelper } from "../../utils/src";
import { handleScrollWhenInputIsFocused } from "@fitinsur/utils/src/scroll-input-focus";

export const InputSize = {
  SMALL: 1,
  NORMAL: 2,
};

const propTypes = {
  response: PropTypes.bool,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  leftIcon: PropTypes.string,
  rightIcon: PropTypes.string,
  leftRender: PropTypes.func,
  rightRender: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  format: PropTypes.func,
  parse: PropTypes.func,
  field: PropTypes.any.isRequired,
  form: PropTypes.any.isRequired,
  isRequired: PropTypes.bool,
  tabIndex: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onPressEnter: PropTypes.func,
  canGenerate: PropTypes.bool,
  fieldProps: PropTypes.any,
  smallInput: PropTypes.bool,
  readOnly: PropTypes.bool,
  inline: PropTypes.bool,
  inverseBlue: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(InputSize)),
  valueCalendar: PropTypes.any,
  topReduce: PropTypes.bool,
};

const defaultProps = {
  canGenerate: true,
  response: false,
  type: "text",
  placeholder: "",
  leftIcon: null,
  rightIcon: null,
  validation: true,
  leftRender: null,
  rightRender: null,
  label: null,
  format: (value) => value,
  parse: (value) => value,
  isLoading: false,
  isRequired: false,
  tabIndex: 0,
  onChange: (value) => value,
  onBlur: (value) => value,
  onFocus: (value) => value,
  onPressEnter: (value) => value,
  fieldProps: {},
  smallInput: false,
  readOnly: false,
  inline: false,
  inverseBlue: false,
  size: InputSize.NORMAL,
  valueCalendar: null,
  topReduce: false,
};

// Mixins
const errorInputMixin = css`
  box-shadow: 0 0 0 3px ${Colors.DANGER};
  &:focus {
    box-shadow: 0 0 0 3px ${Colors.DANGER};
  }
`;

const FontIcon = styled(({ isOpen, ...props }) => <Icon {...props} />)`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Colors.GRAY[0]};
  transition: transform 0.3s ease;
  ${({ isOpen }) =>
    isOpen &&
    css`
      color: ${Colors.SECONDARY};
      transform: rotate(180deg);
    `}
    
  margin: 1px;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    margin: 10px 6px 10px 10px;
  }
`;

const inverseBlueInputMixin = css`
  background-color: transparent;
  color: ${Colors.WHITE};
  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
  &:active:not(:disabled) {
    background-color: ${Colors.PRIMARY};
  }
`;

const inverseBlueContainerMixin = css`
  background: none;
  border-color: ${Colors.WHITE};
  & input {
    ${inverseBlueInputMixin}
    &[disabled] {
      background: none;
    }
  }
`;

const smallInputMixin = css`
  height: 32px;
`;

// Components
export const InputContainer = styled.div`
  box-sizing: border-box;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  box-shadow: 0 0 0 1px ${Colors.GRAY[3]};
  transition: box-shadow 0.15s ease;
  margin-bottom: 10px;
 
  ${({ isOpen }) =>
    isOpen &&
    css`
      box-shadow: 0 0 0 3px ${Colors.SECONDARY};
      
      ${Input} {
        background: ${Colors.GRAY[5]}
        box-shadow: inset 0px 2px 6px rgba(0, 0, 0, 0.2);
      }
    `}
  ${({ inline }) =>
    inline &&
    css`
      min-width: 115px;
      width: 80%;
    `}
  
  ${({ meta }) => (meta && meta.touched && meta.error ? errorInputMixin : "")}
  ${({ inverseBlue }) => inverseBlue && inverseBlueContainerMixin}
    
  @media screen and (min-width: ${Breakpoints.md}) {
    margin-bottom: 0;
  }
`;

export const Option = styled.li`
  list-style: none;
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid ${Colors.GRAY[3]};
  
  ${({ selected }) =>
    selected &&
    css`
      background-color: ${Colors.Alpha(Colors.SECONDARY, 0.7)};
    `}
    
   ${mediaHelper.sm`
      min-height: 42px;
   `}
`;

/* &:hover {
  background-color: ${Colors.GRAY[4]};
} */
export const OptionsContainer = styled.ul.attrs({ role: 'tablist' })`
  position: absolute;
  z-index: 90;
  top:${props => props.topReduce ? "40px" : "63px"};
  max-height: 320px;
  left: 0;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-left: 3px solid ${Colors.SECONDARY};
  border-right: 3px solid ${Colors.SECONDARY};
  border-bottom: 3px solid ${Colors.SECONDARY};
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  
  ${mediaHelper.sm`
      top:${props => props.topReduce ? "40px" : "63px"};
  `}
`;

export const Input = styled.input`
  width: ${({ smallInput }) => (smallInput ? "40px" : "100%")};
  min-width: 0;
  padding: 4px;
  border: 0;
  background: white;
  appearance: none;
  font-size: 14px;
  color: ${Colors.GRAY[0]};
  outline: none;
  box-sizing: border-box;
  text-align: ${({ alignCenter }) => (alignCenter ? "center" : "initial")};
  ${FiraSansBold}
  transition: background 0.15s ease, box-shadow 0.15s ease;
  
  height: 42px;
  min-height: unset;
  
  @media screen and (min-width: ${Breakpoints.sm}) {
    height: 42px;
  }
  
  &::placeholder {
    color: ${Colors.GRAY[3]};
  }

  & .Select__indicator Select__dropdown-indicator {
    border-color: transparent transparent red;
  }
  
  &[disabled] {
    background-color: ${Colors.GRAY[4]};
    color: ${Colors.GRAY[0]};
  }
  
  ${({ size }) => size && _getInputSizeMixin(size)}
`;

export const IconButton = styled(Button).attrs({
  type: "button",
  tabIndex: -1,
})`
  padding: 0;
  & ${Icon} {
    font-size: 16px;
    color: ${Colors.GRAY[3]};
    margin: 0 12px;
  }
`;

const InputSpinner = styled(Spinner)`
  margin: 0 12px;
`;

const Wrapper = styled.div`
  position: relative;
  
  ${({ inline }) =>
    inline &&
    css`
      display: flex;
    `}
  ${({ disabled, inverse }) =>
    disabled &&
    inverse &&
    css`
      opacity: 0.3;
    `}
`;

const _getInputSizeMixin = (size) => {
  if (size === InputSize.SMALL) {
    return smallInputMixin;
  }

  return "";
};

const ComboBox3 = ({
  response,
  type,
  placeholder,
  disabled,
  readOnly,
  leftIcon,
  leftIconClick,
  rightIcon,
  rightIconClick,
  leftRender,
  rightRender,
  isLoading,
  label,
  field,
  format,
  parse,
  size,
  setLabelMinHeight,
  fieldProps,
  isRequired,
  tabIndex,
  classname,
  form: { setFieldValue, setFieldTouched, touched, errors },
  inputProps,
  validation,
  validationMinHeight,
  hideLabelAsterisk,
  smallInput,
  canGenerate,
  alignCenter,
  inline,
  inverseBlue,
  onInputRef,
  onChange,
  onBlur,
  onFocus,
  onPressEnter,
  onSearchSelect,
  options,
  topReduce,
  defaultValue,
  /*  valueCalendar, */
  ...props
}) => {


  const inputRef = useRef(null);
  const optionsRef = useRef(null);
  const selectionStart = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  /* const [showCalendar, setCalendar] = useState(false); */
  const [isOpen, setIsOpen] = useState(false);

  const [optionList, setOptionList] = useState(placeholder ? [{ label: placeholder, value: "" }, ...options] : [{ label: "", value: "" }, ...options]);

  const filteredList = option => {
    let options2 = placeholder ? [{ label: placeholder, value: "" }, ...options] : [{ label: "", value: "" }, ...options];
    if (option == '') {
      let i = labelValue ? (placeholder ? [{ label: placeholder, value: "" }] : [{ label: "", value: "" }]) : [];
      setOptionList([...options2]);
      return;
    }
    let list = ([...options2]).filter(x => x.label.toUpperCase().indexOf(option.toUpperCase()) == -1 ? false : true);
    setOptionList(list);
  };


  useEffect(() => {
    /* if (field.value) {
        let opt = options.find(x => x.value == field.value);
        setLabelValue(opt.label);
    } */
    if (!field.value) {
      if (!isOpen) {
        if (defaultValue) {
          let opt = options.find(x => _.isEqual(x.value, defaultValue));
          if (opt) {
            setLabelValue(opt.label);
            setFieldValue(field.name, opt.value);
          }
        } else {
          setLabelValue(placeholder || '');
        }
      }
    } else {
      let opt = options.find(x => _.isEqual(x.value, field.value));
      if (opt) setLabelValue(opt.label);

    }
  }, [field.value, isOpen, options, defaultValue]);

  useEffect(() => {
    if (
      inputRef &&
      inputRef.current &&
      inputRef.current.selectionStart &&
      (selectionStart.current || selectionStart.current === 0)
    ) {
      inputRef.current.selectionStart = selectionStart.current;
      inputRef.current.selectionEnd = selectionStart.current;
    }
  }, []);

  var touchedList;
  var touchedItem;
  var errorList;
  var errorItem;

  const metaFields = field.name.split('.');
  if (metaFields.length == 3) {
    touchedList = touched[metaFields[0]]
    if (touchedList) {
      touchedItem = touchedList[parseInt(metaFields[1])]
    }
    errorList = errors[metaFields[0]]
    if (errorList) {
      errorItem = errorList[parseInt(metaFields[1])]
    }


  }

  const meta = {

    touched: metaFields.length == 3 && touchedItem ? touchedItem[metaFields[2]] || touched[field.name] : touched[field.name],
    error: metaFields.length == 3 && errorItem ? errorItem[metaFields[2]] || errors[field.name] : errors[field.name],
    isFocused,
    isOpen
  };



  const [labelValue, setLabelValue] = useState('');
  const _onFocus = () => {
    setIsFocused(true);
  };

  const _onBlur = () => {
    setIsFocused(false);
  };

  /*   useEffect() */
  /*   const node = ReactDOM.findDOMNode(props); */
  const [selectedResponse, setSelectedResponse] = useState(true);
  const [cursor, setCursor] = useState(0);
  const current_selection = useRef(null);
  const [onHover, setOnHover] = useState(false);
  const [timer, setTimer] = useState(false);
  useEffect(() => {

    if (current_selection.current && !onHover) {
      optionsRef.current.scrollTo(0, current_selection.current.offsetTop);
      /*  if(current_selection.current.offsetTop > optionsRef.current.offsetHeight){
         current_selection.current.scrollIntoView({block: "center", behavior: "instant"})
       } */

    }

  }, [cursor])

  useEffect(() => {

    if (isOpen) {
      //inputRef.current.focus();
    }

  }, [isOpen])


  return (
    <Wrapper
      inline={inline}
      disabled={disabled}
      inverse={inverseBlue}
      {...props}
    /*   onFocus={() => {
        setIsOpen(true);
 
      }}
      onBlur={() => {
        setTimeout(()=>{ setIsOpen(false)},150)
       
 
      }} */
    >
      {(label || setLabelMinHeight) && (
        <Label
          meta={meta}
          isRequired={isRequired}
          weight={FontWeight.SemiBold}
          setMinHeight={setLabelMinHeight}
          inline={inline}
          inverse={inverseBlue}
          hideLabelAsterisk={hideLabelAsterisk}
        >
          {label}
        </Label>
      )}
      <InputContainer
        meta={meta}
        isFocused={meta.isFocused}
        isOpen={isOpen}
        inline={inline}
        inverseBlue={inverseBlue}

      >
        {leftIcon && (
          <IconButton onClick={leftIconClick}>
            <Icon name={leftIcon} />
          </IconButton>
        )}
        {leftRender && leftRender()}

        <Input
          /*         required={isRequired} */

          autocomplete="off"
          autoComplete={'off'}
          ref={(ref) => {
            inputRef.current = ref;
            if (onInputRef) onInputRef(ref);
          }}
          size={size}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          tabIndex={tabIndex}
          {...field}
          /* onKeyDown={(e) => {
              if (e.key.toLowerCase() === "enter") {
                  onPressEnter(field.value);
              }
          }} */
          onKeyDown={(e) => {

            // arrow up/down button should select next/previous list element
            if (e.key.toLowerCase() === "arrowup" && cursor > 0) {
              setCursor(cursor - 1);
              setOnHover(false)
            } else if (e.key.toLowerCase() === "arrowdown" && cursor < (optionList.filter(x => x.value != field.value)).length - 1) {
              setCursor(cursor + 1);
              setOnHover(false)
            } else if (e.key.toLowerCase() === "arrowup") {
              setCursor((optionList.filter(x => x.value != field.value)).length - 1);
              setOnHover(false)
            } else if (e.key.toLowerCase() === "arrowdown") {
              setCursor(0);
              setOnHover(false)
            } else if (e.key.toLowerCase() === "enter") {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(false);
              /*   setFieldValue(field.name, x.value); */
              inputRef.current.blur();
              /*  setLabelValue(x.label); */
              onChange((optionList.filter(x => x.value != field.value))[cursor].value);
              /*       onSearchSelect(x); */
              setSelectedResponse(true);
            }
          }}
          onFocus={(e) => {
            setIsOpen(true);
            setCursor(0);
            filteredList("");
            if (!selectedResponse) {
              setLabelValue('');
            }

            handleScrollWhenInputIsFocused(e);
            onFocus(field.value);
            _onFocus();
          }}
          onBlur={(e) => {

            e.stopPropagation();
            if (!selectedResponse) {
              setLabelValue('');
              setFieldValue(field.name, '');
            }
            setIsOpen(false)
            onBlur(field.value, this);
            //_onBlur();

            if (!meta.touched) {
              setFieldTouched(field.name, true);
            }
          }}
          smallInput={smallInput}
          inverseBlue={inverseBlue}
          alignCenter={alignCenter}
          value={labelValue}
          onChange={(e) => {
            setLabelValue(e.target.value);
            filteredList(e.target.value);
            setFieldValue(field.name, '');
            setSelectedResponse(false);

            /*    let currentSelection = e.target.selectionStart;
               const event = { ...e };
               const origValue = e.target.value;
               const maskedPrevValue = format(field.value) || ""; */

            // Note - we call format function to clear the input value
            // to remove exceeded characters
            //if (type!=="file"){
            /*       event.target.value = parse(format(origValue)); */
            //}
            /*       const maskedValue = format(event.target.value);
      
                  const diffCaretPosistion =
                    maskedValue.length - maskedPrevValue.length;
                  currentSelection += diffCaretPosistion > 1 ? diffCaretPosistion : 0; */

            // Temporary comment because it doesn't changes the value based on the mask
            // field.onChange(event);
            //if (type!=="file"){
            /* setFieldValue(field.name, parse(format(origValue))); */

            /* onChange(parse(format(origValue))); */

            //} else {
            //  setFieldValue(field.name, event.currentTarget.files[0]);
            //}
            /*  selectionStart.current = currentSelection; */
          }}
          {...fieldProps}
        />
        {/* {rightIcon && (
          <IconButton onClick={rightIconClick}>
            <Icon name={"search"} />
            </IconButton>
          )} */}
        <FontIcon
          onMouseDown={(e) => {
            if (disabled) return;

            if (!topReduce) {
              return setIsOpen(!isOpen);

            }
            else {
              if (!isOpen && !timer) {
                setTimeout(() => { setIsOpen(true); }, 100);

              }
              else {
                if (isOpen) {

                  setIsOpen(false);
                  setTimer(true)
                  setTimeout(() => { setTimer(false) }, 300);
                }

              }
            }


            /* if(p) inputRef.current.focus(); */
            /*  if(isOpen == true){
               setIsOpen(false);
               inputRef.current.blur();
               return;
             }else{
               inputRef.current.focus();
             }
              inputRef.current.focus(); */
          }} name="arrow-down" isOpen={meta.isOpen} />


        {rightRender && rightRender()}
        {isLoading && <InputSpinner size={SpinnerSize.SMALL} />}

      </InputContainer>
      {(isOpen && optionList?.length) > 0 && (
        <OptionsContainer
          ref={(ref) => {
            optionsRef.current = ref;
          }}
          topReduce={topReduce || false}
        >
          {(optionList
            .filter(x => x.value != field.value)
            .filter(x => !x.answerOptionExpireDate))
            .map((x, i) => (
              <Option key={`opt-${x.label}-${i}`}
                onMouseOver={() => { setCursor(i); setOnHover(true) }}
                ref={(e) => cursor === i ? current_selection.current = e : null}
                style={cursor === i ? { backgroundColor: Colors.GRAY[4] } : {}}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(false);
                  setFieldValue(field.name, x.value);
                  inputRef.current.blur();
                  /*  setLabelValue(x.label); */
                  onChange(x.value);
                  /*       onSearchSelect(x); */

                  setSelectedResponse(true);
                }}
              >
                <Text weight={FontWeight.Regular}  >
                  {x.label} {/* {cursor === i ? 'active' : null} */}
                </Text>
              </Option>
            ))}
        </OptionsContainer>
      )}
      {validation && !isOpen && (
        <ValidationError
          meta={meta}
          validationMinHeight={validationMinHeight}
        />
      )}
    </Wrapper>
  );
};

ComboBox3.propTypes = propTypes;
ComboBox3.defaultProps = defaultProps;

export default ComboBox3;
